import { useState } from "react"
import { Redirect } from "react-router-dom"
import styled from "styled-components"

import {
  Banner,
  Checkbox,
  Select,
  spacing,
  Text,
  TextArea,
} from "@minuthq/meatball-ui-react"

import { usePostTrackingBetaEnrolledEvent } from "src/data/analytics/queries/taskManagementAnalyticsQueries"
import { useBackendFlags } from "src/data/flags/useBackendFlags"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { usePutEnrollBeta } from "src/data/taskManagement/taskManagementQueries"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { MButton } from "src/ui/Button/MButton"
import ApartHotelBedLandscape from "src/ui/images/ai-gen-aparthotel-landscape.jpg"
import ApartHotelBedPortrait from "src/ui/images/ai-gen-aparthotel-portrait.jpg"
import { BackdropView } from "src/ui/Layout/BackdropView"
import { InternalLink } from "src/ui/Link/InternalLink"

export function TaskManagementBetaSignup() {
  const { tPlaceholder } = useTranslate()
  const { orgId } = useOrganization()
  const { task_management_released } = useBackendFlags()

  const [challenges, setChallenges] = useState("")
  const [benefits, setBenefits] = useState("")
  const [teamSize, setTeamSize] = useState("")
  const employeesString = tPlaceholder("employees")

  const teamSizeOptions = [
    {
      label: "",
      value: "",
      hidden: true,
    },
    {
      label: "1-5 " + employeesString,
      value: "1-5",
    },
    {
      label: "5-50 " + employeesString,
      value: "5-50",
    },
    {
      label: "50-100 " + employeesString,
      value: "50-100",
    },
    {
      label: "100+ " + employeesString,
      value: "100+",
    },
  ]

  const [isChecked, setIsChecked] = useState(false)

  const putEnroll = usePutEnrollBeta()
  const postTrackingBetaEnrolledEvent = usePostTrackingBetaEnrolledEvent()

  function putEnrolled() {
    putEnroll.mutate({
      path: {
        organization_id: orgId,
      },
    })
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    postTrackingBetaEnrolledEvent.mutate(
      {
        challenges,
        benefits,
        teamSize,
      },
      {
        onSuccess: () => {
          putEnrolled()
        },
      }
    )
  }

  const isError = putEnroll.isError || postTrackingBetaEnrolledEvent.isError

  if (task_management_released) {
    return <Redirect to={Routes.TaskManagement.location().pathname} />
  }

  return (
    <BackdropView
      landscapeImageUrl={ApartHotelBedLandscape}
      portraitImageUrl={ApartHotelBedPortrait}
      title="Tasks"
    >
      <form onSubmit={handleSubmit}>
        <Content>
          <Section>
            <Text variant="heading2">
              {tPlaceholder("Apply for Tasks Beta program")}
            </Text>
            <Text variant="body">
              {tPlaceholder(
                "Streamline maintenance and cleaning flows. Try for free for a limited time with Beta access."
              )}
            </Text>
          </Section>
          <Section>
            <Text variant="subtitle">
              {tPlaceholder(
                "What are your main challenges with managing tasks and property operations?"
              )}
            </Text>
            <TextArea
              onChange={setChallenges}
              value={challenges}
              maxLength={500}
              minRows={4}
              required
              placeholder={tPlaceholder("What keeps you up at night?")}
            />
          </Section>
          <Section>
            <Text variant="subtitle">
              {tPlaceholder("How are you hoping to benefit from Minut tasks?")}
            </Text>
            <TextArea
              onChange={setBenefits}
              value={benefits}
              maxLength={500}
              minRows={4}
              required
              placeholder={tPlaceholder("What do you need help with?")}
            />
          </Section>
          <Section>
            <Text variant="subtitle">
              {tPlaceholder("How large is your team?")}
            </Text>
            <Select
              value={teamSize}
              onChange={(selectedValue) => setTeamSize(selectedValue)}
              options={teamSizeOptions}
              required
              label={tPlaceholder("Select team size*")}
            />
          </Section>
          <Section>
            <Banner type="info">
              <Checkbox
                label={tPlaceholder(
                  "I want to sign up to the Task Beta program and provide feedback on this feature."
                )}
                required
                checked={isChecked}
                onCheck={setIsChecked}
              />
            </Banner>
          </Section>
          {isError && (
            <Section>
              <Banner type="error">
                {tPlaceholder("An error occurred. Please try again.")}
              </Banner>
            </Section>
          )}
        </Content>

        <ButtonContainer>
          <InternalLink to={Routes.TaskManagementBetaStart.location()}>
            {tPlaceholder("Close")}
          </InternalLink>
          <MButton type="submit">{tPlaceholder("Join Beta")}</MButton>
        </ButtonContainer>
      </form>
    </BackdropView>
  )
}

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${spacing.XL};
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.L};
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.M};
`
